/**
 * Formatter pour les chaînes de caractères.
 */
const StringFormatter = {
	/**
	 * Traite une chaîne de caractère en la découpant et/ou tronquant selon les paramètres renseignés.
	 *
	 * Si les paramètres fournis ne permettent pas de découper la chaîne entièrement, le dernier morceau
	 * de la chaîne sera tronqué par "...".
	 *
	 * @param value       La chaîne à traiter.
	 * @param splitLength Longueur à partir de laquelle une chaîne doit être découpée.
	 * @param splitTimes  Nombre de fois qu'une chaîne doit être découpée.
	 *
	 * @returns Un objet qui implémente l'interface StringMetadata.
	 */
	splitAndOrTruncate(value: string, splitLength: number, splitTimes: number): StringMetadata {
		if (splitLength <= 0) {
			throw new Error(`Invalid splitLength parameter, ${splitLength} given.`);
		}

		if (splitTimes < 0) {
			throw new Error(`Invalid splitTimes parameter, ${splitTimes} given.`);
		}

		value = value.trim();

		if (value.length < splitLength) {
			return {
				raw: value,
				values: [value],
				isTruncated: false
			};
		}

		const rawLeft = value.substr(0, splitLength);
		const rawRight = value.slice(splitLength);
		const offset = rawLeft.lastIndexOf(' ');

		// Si aucun espace n'a été trouvé (mot trop long), on ne le découpe pas et on le tronque directement.
		if (offset <= 0) {
			splitTimes = 0;
		}

		let left = rawLeft.substr(0, offset);
		const right = value.substr(offset).trim();
		const result = [] as string[];

		if (0 === splitTimes) {
			let isTruncated = true;
			left = `${rawLeft}...`;

			// Si la fin de la ligne est plus petite que l'éllipse, on l'affiche directement.
			if (rawRight.length <= 2) {
				isTruncated = false;
				left = value;
			}

			result.push(left);

			return {
				raw: value,
				values: result,
				isTruncated
			};
		}

		result.push(left);

		const child = this.splitAndOrTruncate(right, splitLength, splitTimes - 1);

		return {
			raw: value,
			values: result.concat(child.values),
			isTruncated: child.isTruncated
		};
	},

	formatPrice(value: number) {
        const formatter = new Intl.NumberFormat('fr-FR', {
			style: 'currency',
			currency: 'EUR'
		});

		return formatter.format(value);
    },

	formatNumber(value: number, options: Intl.NumberFormatOptions = {}): string {
		const formatter = new Intl.NumberFormat('fr-FR', options);

		return formatter.format(value);
	},

	toCurrency(value: number | string): string {
		const num = 'number' === typeof value
			? value
			: parseFloat(String(value).replace(/\s/g, '').replace(',', '.'));

		if (isNaN(num)) return '-';

		const formatted = StringFormatter.formatNumber(num, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		});

		return `${formatted}\u202f€`;
	}
};

export default StringFormatter;

/**
 * Interface représentant une chaîne de caractères qui a pu être découpée et/ou tronquée.
 */
export interface StringMetadata {
	/**
	 * La chaîne de caractères brute.
	 */
	raw: string;

	/**
	 * Un tableau contenant la chaîne de caractères découpée et potentiellement tronquée.
	 */
	values: string[];

	/**
	 * Indique si la chaîne de caractères à été tronquée.
	 */
	isTruncated: boolean;
}
